import { SorterResult } from 'antd/es/table/interface'
import { DrilldownTablePanelData } from 'features/drilldown/components/table/DrilldownTable'
import { PaginatedRolodexTableRecordType } from 'features/drilldown/components/table/PaginatedRolodexTable'
import { useVersionAwareDrilldownPanelData } from 'features/drilldown/data/DrilldownPanelDataContext'
import { useNestedSetter } from 'features/drilldown/utils/useNestedSetter'
import {
  OptionalValueSetterPair,
  ValueSetterPair,
} from 'features/drilldown/utils/valueSetterPair'
import { OntologyRolodexEntryType, useOntology } from 'features/dynamicOntology'
import { createContext, FC, ReactNode, useContext, useMemo } from 'react'

import { DrilldownTableColumnDef } from './DrilldownTableSchema'

export type DrilldownTableExtraColumn = {
  id: string
  name: string
  linked_types?: ReadonlyArray<OntologyRolodexEntryType>
  render: FC<{ rec: PaginatedRolodexTableRecordType }>
}

type TableConfigContextType = OptionalValueSetterPair<
  OntologyRolodexEntryType,
  'groupByType'
> &
  OptionalValueSetterPair<
    ReadonlyArray<DrilldownTableColumnDef>,
    'columnDefs'
  > &
  OptionalValueSetterPair<boolean, 'showFocusColumn'> &
  ValueSetterPair<boolean, 'propagateColumnFilters'> &
  OptionalValueSetterPair<
    Readonly<Record<string, string>>,
    'columnFilterValues'
  > &
  OptionalValueSetterPair<ReadonlyArray<string>, 'selectedRowKeys'> &
  OptionalValueSetterPair<ReadonlyArray<string>, 'enabledExtraColumnIds'> & {
    extraColumns: ReadonlyArray<DrilldownTableExtraColumn>
  } & {
    initialSorts: ReadonlyArray<SorterResult<PaginatedRolodexTableRecordType>>
  }

const DrilldownTableConfigContext = createContext<TableConfigContextType>({
  groupByType: 'unit',
  setGroupByType: undefined,
  columnDefs: [],
  setColumnDefs: undefined,
  showFocusColumn: false,
  setShowFocusColumn: undefined,
  propagateColumnFilters: false,
  setPropagateColumnFilters: () => {},
  columnFilterValues: {},
  setColumnFilterValues: undefined,
  selectedRowKeys: [],
  setSelectedRowKeys: undefined,
  extraColumns: [],
  enabledExtraColumnIds: [],
  setEnabledExtraColumnIds: () => {},
  initialSorts: [],
})

export interface DrilldownTableConfigContextProviderProps {
  allowSetGroupByType?: boolean
  allowSetColumns?: boolean
  initialSortingOpts?: ReadonlyArray<
    SorterResult<PaginatedRolodexTableRecordType>
  >
  extraColumns: ReadonlyArray<DrilldownTableExtraColumn>
}

export const DrilldownTableConfigContextProvider: FC<
  DrilldownTableConfigContextProviderProps & { children: ReactNode }
> = ({
  allowSetGroupByType,
  allowSetColumns,
  children,
  extraColumns,
  initialSortingOpts,
}) => {
  const { panelData, setPanelData } =
    useVersionAwareDrilldownPanelData<DrilldownTablePanelData>()
  const {
    groupByType,
    columns: columnDefs,
    showFocusColumn,
    propagateColumnFilters,
    columnFilterValues,
    selectedRowKeys,
    enabledExtraColumnIds,
  } = panelData
  const setGroupByType = useNestedSetter(setPanelData, 'groupByType')
  const setColumnDefs = useNestedSetter(setPanelData, 'columns')
  const setShowFocusColumn = useNestedSetter(setPanelData, 'showFocusColumn')
  const setPropagateColumnFilters = useNestedSetter(
    setPanelData,
    'propagateColumnFilters'
  )
  const setColumnFilterValues = useNestedSetter(
    setPanelData,
    'columnFilterValues'
  )
  const setSelectedRowKeys = useNestedSetter(setPanelData, 'selectedRowKeys')
  const setEnabledExtraColumnIds = useNestedSetter(
    setPanelData,
    'enabledExtraColumnIds'
  )

  const { fields } = useOntology()
  const safeColumnDefs = useMemo(
    () => columnDefs.filter((cd) => !!fields[cd.field]),
    [columnDefs, fields]
  )

  return (
    <DrilldownTableConfigContext.Provider
      value={{
        groupByType,
        setGroupByType: allowSetGroupByType ? setGroupByType : undefined,
        columnDefs: safeColumnDefs,
        setColumnDefs: allowSetColumns ? setColumnDefs : undefined,
        showFocusColumn,
        setShowFocusColumn,
        propagateColumnFilters,
        setPropagateColumnFilters,
        columnFilterValues,
        setColumnFilterValues,
        selectedRowKeys,
        setSelectedRowKeys,
        enabledExtraColumnIds,
        setEnabledExtraColumnIds,
        extraColumns,
        initialSorts: initialSortingOpts ?? [],
      }}>
      {children}
    </DrilldownTableConfigContext.Provider>
  )
}

export function useDrilldownTableConfig(): TableConfigContextType {
  return useContext(DrilldownTableConfigContext)
}
